import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Select } from 'antd';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../axios/axiosInstance';
import { ActionButton, PageCard, PageShell, PageToolbar } from '../../components/ui';
import {
    createTechnicianRequest,
    fetchAllTechnicianObjects,
    fetchTeethes,
    fetchTechnicians,
    firstApiError,
    formatTechPrice,
} from '../../services/technicianRequestService';
import ToothPicker from './ToothPicker';

function patientLabel(patient) {
    return `${patient.name || ''}${patient.phone ? ` · ${patient.phone}` : ''}${patient.code ? ` · ${patient.code}` : ''}`.trim();
}

function unwrapPatients(payload) {
    const nested = payload?.data;
    if (Array.isArray(nested)) return nested;
    if (Array.isArray(nested?.data)) return nested.data;
    if (Array.isArray(payload)) return payload;
    return [];
}

async function fetchPatientsForSelect() {
    try {
        const response = await axiosInstance.get('/patient/all');
        const list = unwrapPatients(response.data);
        if (list.length) return list;
    } catch {
        /* doctors may only have GET /patients */
    }

    const collected = [];
    let page = 1;
    let last = 1;
    do {
        const response = await axiosInstance.get('/patients', {
            params: { page, pageSize: 100 },
        });
        const list = unwrapPatients(response.data);
        collected.push(...list);
        last = Number(response.data?.meta?.last_page ?? 1) || 1;
        page += 1;
    } while (page <= last && page <= 20);
    return collected;
}

export default function TechnicianRequestCreate() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [saving, setSaving] = useState(false);
    const [technicians, setTechnicians] = useState([]);
    const [objects, setObjects] = useState([]);
    const [patients, setPatients] = useState([]);
    const [patientsLoading, setPatientsLoading] = useState(true);
    const [patientId, setPatientId] = useState(null);
    const [technicianId, setTechnicianId] = useState(null);
    const [objectId, setObjectId] = useState(null);
    const [paramValues, setParamValues] = useState({});
    const [description, setDescription] = useState('');
    const [teethId, setTeethId] = useState(null);
    const [teethList, setTeethList] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const [techs, objs, teeth, people] = await Promise.allSettled([
                fetchTechnicians(),
                fetchAllTechnicianObjects('doctor'),
                fetchTeethes(),
                fetchPatientsForSelect(),
            ]);
            if (cancelled) return;
            if (techs.status === 'fulfilled') setTechnicians(techs.value);
            if (objs.status === 'fulfilled') setObjects(objs.value);
            if (teeth.status === 'fulfilled') setTeethList(teeth.value);
            if (people.status === 'fulfilled') setPatients(people.value);
            setPatientsLoading(false);
            const failed = [techs, objs, people].find((item) => item.status === 'rejected');
            if (failed) {
                const error = failed.reason;
                if (error?.response?.status === 403) toast.error(t('techRequest.forbidden'));
                else toast.error(firstApiError(error) || t('techRequest.loadError'));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [t]);

    const patientOptions = useMemo(
        () =>
            patients.map((patient) => ({
                value: patient.id,
                label: patientLabel(patient),
            })),
        [patients]
    );

    const selectedObject = objects.find((o) => o.id === objectId) ?? null;

    const estimated = useMemo(() => {
        if (!selectedObject) return 0;
        let sum = Number(selectedObject.price || 0);
        (selectedObject.parameters || []).forEach((param) => {
            if (paramValues[param.id]) sum += Number(param.price || 0);
        });
        return sum;
    }, [selectedObject, paramValues]);

    const handleObjectChange = (id) => {
        setObjectId(id ?? null);
        setParamValues({});
    };

    const handleSubmit = async () => {
        if (!patientId) {
            toast.error(t('techRequest.selectPatient'));
            return;
        }
        if (!technicianId) {
            toast.error(t('techRequest.selectTechnician'));
            return;
        }
        if (!objectId || !selectedObject) {
            toast.error(t('techRequest.selectObject'));
            return;
        }
        const requiredMissing = (selectedObject.parameters || []).some(
            (param) => param.is_required !== false && !paramValues[param.id]
        );
        if (requiredMissing) {
            toast.error(t('techRequest.requiredParamsMissing'));
            return;
        }
        if (!description.trim()) {
            toast.error(t('techRequest.descriptionRequired'));
            return;
        }

        const parameters = (selectedObject.parameters || [])
            .filter((param) => paramValues[param.id])
            .map((param) => ({ parameter_id: param.id, value: paramValues[param.id] }));

        setSaving(true);
        setFieldErrors({});
        try {
            const body = {
                patient_id: patientId,
                technician_id: technicianId,
                object_id: objectId,
                parameters,
                description: description.trim(),
            };
            if (teethId != null) body.teeth_id = teethId;
            const created = await createTechnicianRequest(body);
            toast.success(t('techRequest.createdOk'));
            navigate(`/doctor/technician-requests/${created.id}`);
        } catch (error) {
            const code = error?.response?.status;
            const errors = error?.response?.data?.errors;
            if (errors && typeof errors === 'object') {
                const mapped = {};
                Object.entries(errors).forEach(([key, value]) => {
                    mapped[key] = Array.isArray(value) ? value[0] : value;
                });
                setFieldErrors(mapped);
            }
            if (code === 403) toast.error(t('techRequest.forbidden'));
            else toast.error(firstApiError(error) || t('techRequest.saveError'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <PageShell>
            <PageCard>
                <PageToolbar
                    title={t('techRequest.newOrder')}
                    actions={
                        <button
                            type="button"
                            className="text-sm font-semibold text-slate-500 hover:text-[#1D2C7B]"
                            onClick={() => navigate('/doctor/technician-requests')}
                        >
                            {t('common.back')}
                        </button>
                    }
                />
                <div className="space-y-4 px-3 py-4 sm:px-5">
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">{t('common.patient')}</label>
                        <Select
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                String(option?.label || '')
                                    .toLowerCase()
                                    .includes(String(input).toLowerCase().trim())
                            }
                            className="w-full"
                            size="large"
                            loading={patientsLoading}
                            placeholder={t('techRequest.selectPatient')}
                            value={patientId}
                            options={patientOptions}
                            notFoundContent={patientsLoading ? t('common.loading') : t('common.empty')}
                            onChange={(value) => setPatientId(value ?? null)}
                        />
                        {fieldErrors.patient_id && <p className="mt-1 mb-0 text-xs text-red-500">{fieldErrors.patient_id}</p>}
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">{t('techRequest.technician')}</label>
                        <Select
                            showSearch
                            optionFilterProp="label"
                            className="w-full"
                            size="large"
                            placeholder={technicians.length ? t('techRequest.selectTechnician') : t('techRequest.noTechnicians')}
                            value={technicianId}
                            options={technicians.map((tech) => ({
                                value: tech.id,
                                label: `${tech.name}${tech.phone ? ` · ${tech.phone}` : ''}`,
                            }))}
                            onChange={setTechnicianId}
                        />
                        {fieldErrors.technician_id && <p className="mt-1 mb-0 text-xs text-red-500">{fieldErrors.technician_id}</p>}
                    </div>

                    <div>
                        <ToothPicker teethList={teethList} value={teethId} onChange={setTeethId} />
                        {fieldErrors.teeth_id && <p className="mt-1 mb-0 text-xs text-red-500">{fieldErrors.teeth_id}</p>}
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">{t('techRequest.object')}</label>
                        <Select
                            showSearch
                            optionFilterProp="label"
                            className="w-full"
                            size="large"
                            placeholder={objects.length ? t('techRequest.selectObject') : t('techRequest.noObjects')}
                            value={objectId}
                            options={objects.map((obj) => ({
                                value: obj.id,
                                label: `${obj.name} · ${formatTechPrice(obj.price)} ${t('common.uzs')}`,
                            }))}
                            onChange={handleObjectChange}
                        />
                        {fieldErrors.object_id && <p className="mt-1 mb-0 text-xs text-red-500">{fieldErrors.object_id}</p>}
                    </div>

                    {selectedObject && (
                        <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                            {(selectedObject.parameters || []).map((param) => (
                                <div key={param.id}>
                                    <label className="mb-1 block text-sm font-medium text-slate-700">
                                        {param.name}
                                        {param.is_required !== false ? ' *' : ''}
                                        <span className="ml-2 text-xs font-normal text-slate-400">
                                            {formatTechPrice(param.price)} {t('common.uzs')}
                                        </span>
                                    </label>
                                    <Select
                                        allowClear={param.is_required === false}
                                        className="w-full"
                                        placeholder={param.name}
                                        value={paramValues[param.id]}
                                        options={(param.options || []).map((option) => ({ value: option, label: option }))}
                                        onChange={(value) =>
                                            setParamValues((prev) => {
                                                const next = { ...prev };
                                                if (value == null) delete next[param.id];
                                                else next[param.id] = value;
                                                return next;
                                            })
                                        }
                                    />
                                </div>
                            ))}
                            <p className="mb-0 text-sm text-slate-600">
                                {t('techRequest.estimatedTotal')}:{' '}
                                <strong className="tabular-nums text-[#1D2C7B]">
                                    {formatTechPrice(estimated)} {t('common.uzs')}
                                </strong>
                            </p>
                            <p className="mb-0 text-xs text-slate-400">{t('techRequest.snapshotNote')}</p>
                        </div>
                    )}

                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">{t('techRequest.description')}</label>
                        <Input.TextArea
                            rows={4}
                            maxLength={5000}
                            value={description}
                            placeholder={t('techRequest.descriptionPh')}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                        {fieldErrors.description && <p className="mt-1 mb-0 text-xs text-red-500">{fieldErrors.description}</p>}
                    </div>

                    <ActionButton
                        collapseOnMobile={false}
                        loading={saving}
                        className="!h-11 w-full sm:!w-auto"
                        onClick={handleSubmit}
                    >
                        {t('techRequest.newOrder')}
                    </ActionButton>
                </div>
            </PageCard>
        </PageShell>
    );
}
